import {
  ParlorHeadSection,
  BTypeMainHead,
  FUNERAL_SIDO,
  GOI_WEB_URL,
  FUNERAL_SIGUNGU,
  CTypeSangjoService,
  DTypeParlorList,
  BTypeCardView,
  CTypeBreadCrumb,
  FullWidthButton,
  LEGAL_TEXTS,
  colors,
  FixedBottomButton,
  BinsoPriceTable,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";
import { ParlorDetailProps, ATypeTabs } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import Head from "next/head";
import { Box, Stack } from "@mui/material";

const { regionColor, targetSidos } = parlorRegion;

const BreakLine = () => {
  return (
    <Box padding="0 20px">
      <Box
        width="100%"
        height="1px"
        style={{ background: colors.gray900 }}
        margin="0 0 20px"
        padding="0 20px"
      />
    </Box>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        id: String(parlor.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      const descParts = [
        `${parlor.companyname} 장례 서비스를 안내합니다`,
        `고이장례연구소에서 e하늘 장사정보 기반으로 안내드립니다`,
        `주소 : ${parlor.address}`,
        `장례지도사 동행 서비스`,
        `임종 준비부터 장례 마무리까지`,
      ].filter(Boolean);
      description = descParts.join(' | ');
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname} 장례 서비스, ${parlor?.companyname} 얼마, ${parlor?.companyname} 앰뷸런스, ${parlor?.companyname} 수의, ${parlor?.companyname} 유골함, ${parlor?.companyname} 도우미, ${parlor?.companyname} 비용, ${parlor?.companyname} 할인, ${parlor?.companyname} 전화번호`,
      pageOgTitle: `${parlor?.companyname} 장례 서비스 안내·소개 (${district}) | 강원 장례정보 [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export default function flower({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionParlors = parlors.filter((res) => targetSidos.includes(res.sido));
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu)
  );

  const carouselItems = regionParlors.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gangwon-portal.co.kr/gangwon-parlor-places/${res.id}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionParlors.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <CTypeBreadCrumb
        firstItem={{
          text: `장례식장 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "gangwon",
            path: "prlr_service",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `${sidoName} 장례식장`,
          href: `/gangwon-parlors/${sidoName}도 의 장례식장들/`,
          event: {
            domain: "gangwon",
            path: "prlr_service",
            trigger_and_target: "br_crumb_sido_prlr_list",
          },
        }}
        thirdItem={{
          text: `${sigunguName} 장례식장`,
          href: `/gangwon-parlors/${sidoName}도-${sigunguName} 의 장례식장들/`,
          event: {
            domain: "gangwon",
            path: "prlr_service",
            trigger_and_target: "br_crumb_sigungu_prlr_list",
          },
        }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "prlr_service",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ParlorHeadSection
        component={
          <>
            <BTypeMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              mainColor={regionColor}
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname} 상조 서비스`}
              managed_type={defaultParlor.managed_type}
            />
            <BreakLine />
            <Stack width="100%" padding="0 20px">
              <Box className="subtitle_14_b gray900" marginBottom="12px">
                {defaultParlor.companyname} 예상 빈소 이용료와, 고이 상조 이용 시 
                전체 예상 장례 비용(자체 추산)을 안내해드립니다.
              </Box>
              <FullWidthButton
                text="예상 총 장례 비용 확인하기"
                backgroundColor={regionColor.c300}
                href={GOI_WEB_URL + `/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
                event={{
                  domain: "dggb",
                  path: "prlr_item_service",
                  trigger_and_target: "CTA_expected_cost",
                }}
              />
              <Box className="caption_11_r gray700" whiteSpace="pre-line" margin="12px 0">
                {LEGAL_TEXTS.UNRELATED}
              </Box>
            </Stack>
          </>
        }
      />
      <Stack style={{ background: "white" }}>
        <ATypeTabs
          mainColor={regionColor}
          href={`gangwon-parlor-places/${defaultParlor.id}`}
          selectTab="상조 서비스"
          defaultParlor={defaultParlor}
          event={{
            domain: "gangwon",
            path: "prlr_service",
            trigger_and_target: "AType_tab",
          }}
        />
      </Stack>

      <Stack style={{ background: regionColor.c100 }} padding="0 20px">
        <Box className="subtitle_16_b gray900" padding="12px 0 4px">
          빈소 이용료
        </Box>
        <BinsoPriceTable
          defaultParlor={defaultParlor}
          mainColor={regionColor}
        />
        <Box className="caption_11_r gray700" whiteSpace="pre-line" margin="12px 8px 20px">
          {LEGAL_TEXTS.COST_VARIABILITY}
        </Box>
      </Stack>
      <Stack>
        <Box className="subtitle_16_b gray900" padding="12px 20px 0">
          고이 상조 이용 시 상조 비용
        </Box>
        <CTypeSangjoService
          defaultParlor={defaultParlor}
          mainColor={regionColor}
          event={{
            domain: "gangwon",
            path: "prlr_service",
            trigger_and_target: "goi_call",
          }}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <DTypeParlorList
          binsoAveragePrice
          sido={defaultParlor.sido}
          sigungu={String(defaultParlor.sigungu)}
          currentParlorId={defaultParlor.id}

          currentLat={Number((defaultParlor as any).latitude)}

          currentLng={Number((defaultParlor as any).longitude)}
          title={`${sidoName}의 다른 장례식장`}
          CardViewType={(res) => (
            <BTypeCardView
              descriptions={[
                ...(res.distanceLabel ? [`${defaultParlor.companyname}에서 ${res.distanceLabel}`] : []),
                `빈소 이용료: ${res.binsos[0].charge.toLocaleString()}원부터`,
              ]}
              mainColor={regionColor}
              key={res.companyname}
              name={res.companyname}
              href={`/gangwon-parlor-places/${res.id}/`}
              imgSrc={res.images[0] ? res.images[0].url : ""}
            />
          )}
        />
      </Stack>
      <FixedBottomButton 
        text="예상 총 장례 비용 확인하기" 
        backgroundColor={regionColor.c300}
        href={GOI_WEB_URL + `/match/parlor-requests/${defaultParlor.id}/?utm_source=dogsound`}
        event={{
          domain: "dggb",
          path: "prlr_compare",
          trigger_and_target: "Fixed_CTA",
        }} />
    </>
  );
}
